import * as React from 'react';
import {
  Tabs as RACTabs,
  TabList as RACTabList,
  Tab as RACTab,
  TabPanel as RACTabPanel,
  type TabsProps as RACTabsProps,
  type TabListProps as RACTabListProps,
  type TabProps as RACTabProps,
  type TabPanelProps as RACTabPanelProps,
} from 'react-aria-components';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/cn';

/**
 * Klaret Tabs (handoff §10.7).
 * Page-level 36 px text tabs (body), active = body-medium --tvl-ink + 2 px claret underline
 * flush to the tab-row hairline; inactive hover = --tvl-ink-2 + wash.
 * Inside cards use a segmented control instead.
 */

interface TabsContextValue {
  variant: 'line' | 'pill';
}
const TabsCtx = React.createContext<TabsContextValue>({ variant: 'line' });

export interface TabsProps extends Omit<RACTabsProps, 'className'> {
  variant?: 'line' | 'pill';
  className?: string;
}

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ className, variant = 'line', ...props }, ref) => (
    <TabsCtx.Provider value={{ variant }}>
      <RACTabs
        ref={ref}
        data-variant={variant}
        className={cn('flex flex-col gap-3', className)}
        {...props}
      />
    </TabsCtx.Provider>
  ),
);
Tabs.displayName = 'Tabs';

const tabsListVariants = cva('inline-flex items-center', {
  variants: {
    variant: {
      // overflow-x-auto lets a wide tab row scroll horizontally, but CSS promotes
      // the paired overflow-y from `visible` to `auto`, and the trigger's `-mb-px`
      // (which sits the active underline on the row hairline) overhangs by 1px —
      // so the row grows a phantom vertical scrollbar. Pin overflow-y to `clip`
      // (never scrolls) with a clip-margin so the underline + focus ring still paint.
      line: 'gap-0 border-b border-[var(--tvl-hairline)] w-full overflow-x-auto overflow-y-clip [overflow-clip-margin:6px]',
      pill: [
        'gap-1 rounded-[var(--tvl-r2)] p-1',
        'bg-[var(--tvl-inset)]',
      ].join(' '),
    },
  },
  defaultVariants: { variant: 'line' },
});

export interface TabsListProps
  extends Omit<RACTabListProps<object>, 'className'>,
    VariantProps<typeof tabsListVariants> {
  className?: string;
}

export const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
  ({ className, variant, ...props }, ref) => {
    const ctx = React.useContext(TabsCtx);
    const v = variant ?? ctx.variant;

    // Keep the selected tab on screen. The `line` row scrolls horizontally when
    // it does not fit, which on a phone it usually does not — so the selected
    // tab could sit entirely outside the visible strip and the user had no way
    // to tell where they were without scrolling the row hunting for it.
    const localRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        localRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );

    React.useEffect(() => {
      const list = localRef.current;
      if (!list) return;
      const sync = () => {
        const sel = list.querySelector('[data-selected]');
        // `nearest` so a row that already shows the tab does not jump.
        sel?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
      };
      sync();
      // RAC swaps data-selected on the DOM rather than remounting, so an
      // attribute observer is what actually catches a tab change here.
      const mo = new MutationObserver(sync);
      mo.observe(list, { attributes: true, subtree: true, attributeFilter: ['data-selected'] });
      return () => mo.disconnect();
    }, []);

    return (
      <RACTabList
        ref={setRefs}
        className={cn(tabsListVariants({ variant: v }), 'max-sm:no-scrollbar', className)}
        data-scroll-x
        {...props}
      />
    );
  },
);
TabsList.displayName = 'TabsList';

const tabsTriggerVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'text-[14px] leading-[20px]', // body
    'transition-colors duration-[var(--tvl-d-base)]',
    'disabled:pointer-events-none disabled:text-[var(--tvl-ink-disabled)]',
    'outline-none',
    // focus = 2px ink outline (global pattern)
    'focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
  ].join(' '),
  {
    variants: {
      variant: {
        line: [
          'relative h-9 px-3 -mb-px border-b-2 border-transparent',
          'font-normal text-[var(--tvl-ink-2)]',
          'hover:text-[var(--tvl-ink)] hover:bg-[var(--tvl-wash-hover)]',
          // RAC sets data-selected
          'data-[selected]:border-[var(--tvl-accent)] data-[selected]:text-[var(--tvl-ink)] data-[selected]:font-[500]',
          // On touch the 2px underline is a weak signal, and in a row that
          // scrolls horizontally the selected tab is often only half on screen.
          // A tinted pill behind it survives both. Coarse-pointer only, so the
          // desktop tab strip keeps its flat underline-only look.
          '[@media(pointer:coarse)_and_(hover:none)]:rounded-t-[var(--tvl-r2)]',
          '[@media(pointer:coarse)_and_(hover:none)]:data-[selected]:bg-[var(--tvl-selected)]',
        ].join(' '),
        pill: [
          'h-7 rounded-[var(--tvl-r2)] px-3',
          'font-normal text-[var(--tvl-ink-2)]',
          'hover:text-[var(--tvl-ink)]',
          'data-[selected]:bg-[var(--tvl-raised)] data-[selected]:text-[var(--tvl-ink)] data-[selected]:font-[500]',
          'data-[selected]:shadow-[var(--tvl-e1)]',
        ].join(' '),
      },
    },
    defaultVariants: { variant: 'line' },
  },
);

export interface TabsTriggerProps
  extends Omit<RACTabProps, 'className'>,
    VariantProps<typeof tabsTriggerVariants> {
  className?: string;
}

export const TabsTrigger = React.forwardRef<HTMLDivElement, TabsTriggerProps>(
  ({ className, variant, ...props }, ref) => {
    const ctx = React.useContext(TabsCtx);
    const v = variant ?? ctx.variant;
    return (
      <RACTab
        ref={ref}
        data-pop
        className={cn(tabsTriggerVariants({ variant: v }), className)}
        {...props}
      />
    );
  },
);
TabsTrigger.displayName = 'TabsTrigger';

export interface TabsContentProps extends Omit<RACTabPanelProps, 'className'> {
  className?: string;
}

export const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ className, ...props }, ref) => (
    <RACTabPanel
      ref={ref}
      className={cn(
        'outline-none',
        'focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
        className,
      )}
      {...props}
    />
  ),
);
TabsContent.displayName = 'TabsContent';
